import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useFont } from "../../contexts/FontContext/FontContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useLogo } from "../../contexts/LogoContext/LogoContext";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import PostOfficeOption from "../PostOfficeOption/PostOfficeOption";
import PurchaseLabelOption from "../PurchaseLabelOption/PurchaseLabelOption";
import usePortalSession from "../../hooks/usePortalSession";
import useScreenWidth from "../../hooks/useScreenWidth";
import { startEllipsisInterval } from "../utils/common";
import styles from "./SelectReturnOption.module.css";

const SelectReturnOption = () => {
    const [postOfficePrice, setPostOfficePrice] = useState("loading");
    const [isAnimating, setIsAnimating] = useState(true);
    const [showPostOfficeOption, setShowPostOfficeOption] = useState(false);
    const [showPurchaseLabelOption, setShowPurchaseLabelOption] = useState(false);
    const [showOptionTitle, setShowOptionTitle] = useState(false);
    const [showPurchaseLabelBorder, setShowPurchaseLabelBorder] = useState(false);
    const [showPreferredText, setShowPreferredText] = useState(false);
    const [showPrintAtHomeTitle, setShowPrintAtHomeTitle] = useState(false);
    const [showPrintAtHomeBlurb, setShowPrintAtHomeBlurb] = useState(false);
    const [showTrackingIncluded, setShowTrackingIncluded] = useState(false);
    const [showPostOfficeTitle, setShowPostOfficeTitle] = useState(false);
    const [showEstimatedCost, setShowEstimatedCost] = useState(false);
    const [showNoTrackingIncluded, setShowNoTrackingIncluded] = useState(false);
    const [showPurchaseLabelImage, setShowPurchaseLabelImage] = useState(false);
    const [showPostOfficeImage, setShowPostOfficeImage] = useState(false);
    const [startShimmer, setStartShimmer] = useState(false);
    const [hoveredOption, setHoveredOption] = useState(null);
    const [highlightEnabled, setHighlightEnabled] = useState(false);
    const [renderPostOfficeOption, setRenderPostOfficeOption] = useState(false);
    const [renderPurchaseLabelOption, setRenderPurchaseLabelOption] = useState(false);
    const [renderReturnsFeedbackForm, setReturnsFeedbackForm] = useState(true);
    const screenWidth = useScreenWidth();
    const fontSchemes = useFont();
    const fontFamilyOne = fontSchemes.scheme_one;
    const showOptionTitleClasses = showOptionTitle ? `titles fade_in ${fontFamilyOne} ${styles.select_option_title}` : "no_opacity";
    const location = useLocation();
    const itemQuantity = location.state?.quantity;
    const queryParams = new URLSearchParams(location.search);
    const apiUrl = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const token = queryParams.get("token");
    const logoUrl = useLogo();
    const sessionId = usePortalSession(token, apiUrl);

    useEffect(() => {
        if (isAnimating) {
            const interval = startEllipsisInterval(setPostOfficePrice, postOfficePrice);
            return () => clearInterval(interval);
        }
    }, [isAnimating]);

    useEffect(() => {
        const checkReturnStatus = async () => {
            const response = await fetch(`${apiUrl}/api/check_return_status/${token}/`);
            const data = await response.json();        
            if (data.status) { navigate(`/returns_confirm/${data.order_id}?token=${token}`); } // redirect to confirmation page if this page is accessed after a label has been purchased                   
        };
        checkReturnStatus();
    }, []);

    useEffect(() => {
        const highlightTimer = setTimeout(() => setHighlightEnabled(true), 2000);
        return () => clearTimeout(highlightTimer);
    }, []);

    useEffect(() => {
        setTimeout(() => setShowOptionTitle(true), 100);
        setTimeout(() => setShowPurchaseLabelOption(true), 300);
        setTimeout(() => setShowPostOfficeOption(true), 600);
        setTimeout(() => setShowPurchaseLabelImage(true), 800);
        setTimeout(() => setShowPostOfficeImage(true), 800);
        setTimeout(() => setShowPrintAtHomeTitle(true), 1000);
        setTimeout(() => setShowPostOfficeTitle(true), 1000);
        setTimeout(() => setShowEstimatedCost(true), 1300);
        setTimeout(() => setShowPrintAtHomeBlurb(true), 1300);
        setTimeout(() => setShowTrackingIncluded(true), 1500);
        setTimeout(() => setShowNoTrackingIncluded(true), 1500);
        setTimeout(() => setShowPreferredText(true), 2000);
        setTimeout(() => setShowPurchaseLabelBorder(true), 2000);
        setTimeout(() => setStartShimmer(true), 2500);
    }, []);

    useEffect(() => {
        const getPostOfficeEstimate = async () => {
            try {
                if (!isAnimating) { setIsAnimating(true); }
                const response = await fetch(`${apiUrl}/api/create_shipment/${token}/`, { 
                    method: "POST", 
                    headers: { 
                        "Content-Type": "application/json",
                        "X-Portal-Session": sessionId
                    } 
                });
                if (response.status === 409) {
                    alert("This portal was taken over in another window.");
                    navigate("/");
                    return;
                }
                const data = await response.json();
                let selectedMargin = data.selected_margin; //brand's markup
                let { rate } = data.selected_rate;
                rate = parseFloat(rate);
                selectedMargin = parseFloat(selectedMargin);
                const shipwheelMargin = 1.00; //shipwheel's markup
                let postOfficeEstimate = Math.ceil(rate + shipwheelMargin + selectedMargin) //brand keeps what they pick in margin tool
                let postOfficeMarkUp = .5; //arbitrary for now
                postOfficeEstimate = (postOfficeEstimate * postOfficeMarkUp + postOfficeEstimate).toFixed(2);
                setPostOfficePrice(postOfficeEstimate); //mark up 20% from out calculated price
                setIsAnimating(false);
            } catch (error) { 
                console.error("Error fetching shipment rates:", error); 
                alert("Error fetching shipping rates. Your address may be invalid. Please click the left purchase label option and edit your address! Contact support if this doesn't resolve the issue.");
            }
        };
        getPostOfficeEstimate();
    }, []);

    if (renderPostOfficeOption) { return <PostOfficeOption logoUrl={logoUrl} />; }
    if (renderPurchaseLabelOption) { return <PurchaseLabelOption logoUrl={logoUrl} itemQuantity={itemQuantity} />; }
    //if (renderReturnsFeedbackForm) { return <ReturnsFeedbackForm logoUrl={logoUrl} token={token} />; }

    return (
        <div className={`${styles.select_return_option_container} ${fontFamilyOne}`}>
            <Helmet><title>Select option</title></Helmet>
            {screenWidth > 481 ? (<LogoWrapper class_={styles.select_option_logo} src={logoUrl ? `${process.env.PUBLIC_URL}/business_images/${logoUrl}` : `${process.env.PUBLIC_URL}/images/partial_logo.png`} />) : null}
            <p className={`${showOptionTitle ? `titles ${styles.select_option_title} fade_in` : "no_opacity"}`}>Select an option.</p>
            <div className={styles.option_container}>
                <div className={`${showPurchaseLabelOption ? `${styles.options} ${styles.fade_in_options}`: "no_opacity"} ${showPurchaseLabelBorder ? styles.purchase_label_border : ""} ${highlightEnabled && hoveredOption === "purchase" ? styles.purchase_label_border_highlight : ""} ${styles.purchase_label_option}`} onMouseEnter={() => highlightEnabled && setHoveredOption("purchase")} onMouseLeave={() => highlightEnabled && setHoveredOption(null)} onClick={() => setRenderPurchaseLabelOption(true)}>
                    <p className={`${styles.preferred} titles ${showPreferredText ? "fade_in" : "no_opacity"} ${startShimmer ? styles.shimmer : ""}`}>* Preferred *</p>
                    <p className={`${styles.option_titles} ${styles.print_at_home_title} ${showPrintAtHomeTitle ? "fade_in" : "no_opacity"}`}>Print return label at home.</p>
                    <div className={`${styles.print_at_home_blurb_wrapper} ${showPrintAtHomeBlurb ? "fade_in" : "no_opacity"}`}>
                    	<p className={`${styles.print_at_home_blurb}`}>Print label at home and save a trip to the post office!</p>
                    </div>
                    <p className={`${styles.tracking_included} ${showTrackingIncluded ? "fade_in" : "no_opacity"} ${styles.tracking_text}`}>Tracking included!</p>
                    <img src={`${process.env.PUBLIC_URL}/images/purchase_label.png`} alt="purchase_label_image" className={`${styles.purchase_label_option_image} ${showPurchaseLabelImage ? `${styles.option_images} fade_in` : "no_opacity"}`} />
                </div>
                <div className={`${styles.post_office_option} ${showPostOfficeOption ? `${styles.options} ${styles.fade_in_options}` : "no_opacity"} ${highlightEnabled && hoveredOption === "post" ? styles.post_office_border_highlight : ""}`} onMouseEnter={() => highlightEnabled && setHoveredOption("post")} onMouseLeave={() => highlightEnabled && setHoveredOption(null)} onClick={() => setRenderPostOfficeOption(true)}>
                    <p className={`${styles.option_titles} ${styles.post_office_title} ${showPostOfficeTitle ? "fade_in" : "no_opacity"}`}>Return package on your own.</p>
                    <p className={`${styles.estimated_cost} ${showEstimatedCost ? "fade_in" : "no_opacity"}`}>Estimated cost: <span style={{ color: "red", fontWeight: "bold" }}>{`${isAnimating ? "" : "$"}${postOfficePrice}`}</span></p>
                    <p className={`${styles.no_tracking_included} ${showNoTrackingIncluded ? "fade_in" : "no_opacity"} ${styles.tracking_text}`}>No tracking included.</p>
                    <img src={`${process.env.PUBLIC_URL}/images/return_at_post_office.png`} alt="return_at_post_office" className={`${styles.return_at_post_office_option_image} ${showPostOfficeImage ? `${styles.option_images} fade_in` : "no_opacity"}`} />
                </div>
            </div>
        </div>
    );
};

export default SelectReturnOption;